import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

import { api, TASKS_CHANGED } from "../lib/api";
import { Logo, useAuth } from "./Auth";
import { useApi } from "../lib/useApi";

interface NavItem { to: string; label: string; icon: string; badge?: number }

/** Each design keeps its own theme choice ("theme" classic, "theme-new" the redesign), so
 *  switching designs never leaves the classic one on a theme it has no colours for. */
export function useTheme(key = "theme", allowed: string[] = ["auto", "light", "dark"]) {
  const [theme, setTheme] = useState<string>(() => {
    try { const t = localStorage.getItem(key) ?? "auto"; return allowed.includes(t) ? t : "auto"; } catch { return "auto"; }
  });
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "auto") root.removeAttribute("data-theme"); else root.setAttribute("data-theme", theme);
    try { localStorage.setItem(key, theme); } catch { /* private window */ }
  }, [theme, key]);
  return [theme, setTheme] as const;
}

export function Layout({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const overview = useApi<{ issues: { total: number }; documents: Record<string, number>; tasks_open?: number }>("/overview");
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useTheme();
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  const reloadOverview = overview.reload;
  useEffect(() => {
    window.addEventListener(TASKS_CHANGED, reloadOverview);
    return () => window.removeEventListener(TASKS_CHANGED, reloadOverview);
  }, [reloadOverview]);

  const groups: { title?: string; items: NavItem[] }[] = [
    { items: [{ to: "/", label: "Overview", icon: "◧" }, { to: "/tasks", label: "Tasks", icon: "✓", badge: overview.data?.tasks_open || undefined }] },
    { title: "Operations", items: [
      { to: "/stock", label: "Stock", icon: "▦" },
      { to: "/production", label: "Production", icon: "⚙" },
      { to: "/shipments", label: "Shipments", icon: "⛴" },
      { to: "/documents", label: "Documents", icon: "▤", badge: overview.data?.documents?.draft || undefined },
    ] },
    { title: "Sales", items: [
      { to: "/sales", label: "Sales", icon: "↗" },
      { to: "/depletions", label: "Depletions", icon: "⇣" },
      { to: "/customers", label: "Customers", icon: "◉" },
      { to: "/costs", label: "Costs & margin", icon: "$" },
      // The books: only for the people an administrator allows (Settings → People).
      ...(user?.sees_books ? [{ to: "/quickbooks", label: "QuickBooks", icon: "Q" }] : []),
    ] },
    { title: "Catalogue", items: [
      { to: "/products", label: "Products", icon: "◆" },
      { to: "/locations", label: "Locations", icon: "⌂" },
    ] },
    { title: "Data", items: [
      { to: "/data-quality", label: "Data quality", icon: "!", badge: overview.data?.issues.total || undefined },
      { to: "/sources", label: "Integrations", icon: "⇅" },
    ] },
  ];

  return (
    <div className={`app${open ? " nav-open" : ""}`}>
      <aside className="sidebar" aria-label="Main navigation">
        <Link to="/" className="brand" aria-label="Brinley & Company — overview">
          <Logo className="brand-logo" />
          <small>Stock &amp; sales</small>
        </Link>
        <nav>
          {groups.map((g, i) => (
            <div key={i} className="nav-group">
              {g.title && <p className="nav-title">{g.title}</p>}
              {g.items.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.to === "/"} className="nav-link">
                  <span className="nav-icon" aria-hidden="true">{it.icon}</span>
                  <span>{it.label}</span>
                  {it.badge ? <span className="nav-badge" aria-label={`${it.badge} items`}>{it.badge}</span> : null}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-foot">
          <NavLink to="/settings" className="nav-link">
            <span className="nav-icon" aria-hidden="true">⚙</span><span>Settings</span>
          </NavLink>
          <WhoAmI />
          <SwitchDesign to="new" />
          <label className="theme-pick">
            <span>Theme</span>
            <select value={theme} onChange={(e) => setTheme(e.target.value)} aria-label="Theme">
              <option value="auto">System</option><option value="light">Light</option><option value="dark">Dark</option>
            </select>
          </label>
        </div>
      </aside>
      <div className="main">
        <header className="topbar">
          <button className="menu-btn" aria-label="Open menu" onClick={() => setOpen((o) => !o)}>☰</button>
          <GlobalSearch />
        </header>
        <main className="content">{children}</main>
      </div>
      {open && <button className="scrim" aria-label="Close menu" onClick={() => setOpen(false)} />}
    </div>
  );
}

export function WhoAmI() {
  const { user, signOut } = useAuth();
  if (!user) return null;
  return (
    <div className="whoami">
      <NavLink to="/settings?tab=account" className="whoami-name" title={user.email}>
        <span className="avatar" aria-hidden="true">{user.name.slice(0, 1).toUpperCase()}</span>
        <span className="truncate">{user.name}</span>
      </NavLink>
      <button className="btn btn-sm" onClick={() => void signOut()}>Sign out</button>
    </div>
  );
}

interface Hit { kind: string; label: string; sub: string; href: string }

export function GlobalSearch({ placeholder }: { placeholder?: string } = {}) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [active, setActive] = useState(0);
  const nav = useNavigate();
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return; }
    const t = setTimeout(() => api.get<Hit[]>(`/search?q=${encodeURIComponent(q)}`).then(setHits).catch(() => setHits([])), 150);
    return () => clearTimeout(t);
  }, [q]);

  const go = (h: Hit) => { nav(h.href); setQ(""); setHits([]); };

  return (
    <div className="search" ref={box}>
      <input
        type="search" placeholder={placeholder ?? "Search products, customers, documents…"} aria-label="Search"
        value={q} onChange={(e) => { setQ(e.target.value); setActive(0); }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") setActive((a) => Math.min(a + 1, hits.length - 1));
          if (e.key === "ArrowUp") setActive((a) => Math.max(a - 1, 0));
          if (e.key === "Enter" && hits[active]) go(hits[active]);
          if (e.key === "Escape") setHits([]);
        }}
      />
      {hits.length > 0 && (
        <ul className="search-results" role="listbox">
          {hits.map((h, i) => (
            <li key={h.href + i} role="option" aria-selected={i === active}>
              <button onMouseDown={(e) => { e.preventDefault(); go(h); }} className={i === active ? "is-active" : ""}>
                <span className="hit-kind">{h.kind}</span>
                <span className="hit-label">{h.label}</span>
                <span className="hit-sub">{h.sub}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Old and new designs side by side (owner, 4 Nov): each person picks, and can go back. */
export function SwitchDesign({ to, className }: { to: "classic" | "new"; className?: string }) {
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const go = async () => {
    setBusy(true); setErr(null);
    try { await api.post("/auth/ui", { ui: to }); await refresh(); }
    catch (e) { setErr(e instanceof Error ? e.message : "Could not switch"); setBusy(false); }
  };
  return (
    <>
      <button type="button" className={className ?? "btn btn-sm switch-design"} onClick={() => void go()} disabled={busy}>
        {busy ? "Switching…" : to === "new" ? "Try the new design" : "Switch to the old design"}
      </button>
      {err && <p role="alert" className="switch-error">{err}</p>}
    </>
  );
}
